import { useEffect, useRef, useState } from "react";
import { Maximize2, Pause, Play, Volume2, VolumeX } from "lucide-react";

const fmt = (s) => { const t = Math.max(0, Math.floor(s || 0)); const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60; return `${h ? `${h}:` : ""}${String(m).padStart(h ? 2 : 1, "0")}:${String(sec).padStart(2, "0")}`; };

export const YT_EMBED_PARAMS = "controls=0&disablekb=1&fs=0&rel=0&iv_load_policy=3&cc_load_policy=0&modestbranding=1&playsinline=1&enablejsapi=1";

export const YouTubeGuidePlayer = ({ id, title, onProgress }) => {
  const prog = useRef(onProgress);
  prog.current = onProgress;
  const ref = useRef(null);
  const box = useRef(null);
  const [state, setState] = useState(-1);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [muted, setMuted] = useState(false);
  const post = (func, args = []) => ref.current?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args }), "*");
  const hideCaptions = () => { post("unloadModule", ["captions"]); post("unloadModule", ["cc"]); post("setOption", ["captions", "track", {}]); };
  useEffect(() => {
    const onMsg = (e) => {
      if (!String(e.origin).includes("youtube.com")) return;
      try {
        const d = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (d?.event === "onReady") hideCaptions();
        if (d?.event === "onStateChange") { setState(d.info); if (d.info === 1) hideCaptions(); }
        if (d?.event === "infoDelivery" && d.info) {
          if (d.info.currentTime != null) { setCur(d.info.currentTime); prog.current?.(d.info.currentTime, d.info.duration || 0, d.info.playerState === 0); }
          if (d.info.duration) setDur(d.info.duration);
          if (d.info.playerState != null) setState(d.info.playerState);
          if (d.info.muted != null) setMuted(d.info.muted);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onLoad = () => { ref.current?.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: 1, channel: "widget" }), "*"); hideCaptions(); };
  const playing = state === 1 || state === 3;
  const toggle = () => { post(playing ? "pauseVideo" : "playVideo"); hideCaptions(); };
  const seek = (e) => { const r = e.currentTarget.getBoundingClientRect(); const t = ((e.clientX - r.left) / r.width) * dur; post("seekTo", [t, true]); setCur(t); };
  const toggleMute = () => { post(muted ? "unMute" : "mute"); setMuted(!muted); };
  const full = () => (document.fullscreenElement ? document.exitFullscreen?.() : box.current?.requestFullscreen?.());
  return (
    <div ref={box} data-testid="yt-guide-player" className="relative w-full aspect-video rounded-2xl border border-border bg-black overflow-hidden select-none" onContextMenu={(e) => e.preventDefault()}>
      <iframe ref={ref} data-testid="classroom-player" title={title} onLoad={onLoad} src={`https://www.youtube.com/embed/${id}?${YT_EMBED_PARAMS}&origin=${encodeURIComponent(window.location.origin)}`}
        className="absolute left-0 w-full h-[calc(100%+140px)] -top-[70px] pointer-events-none" allow="autoplay; encrypted-media" />
      <button type="button" aria-label={playing ? "Pause" : "Play"} data-testid="yt-guide-toggle" onClick={toggle} className="absolute inset-0 grid place-items-center text-white">
        {!playing && <span className="grid place-items-center w-20 h-20 rounded-full bg-black/50 border border-white/40 backdrop-blur"><Play size={40} /></span>}
      </button>
      <div data-testid="yt-guide-controls" className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-4 pt-8 pb-3 text-white">
        <div data-testid="yt-guide-progress" onClick={seek} className="h-1.5 w-full rounded-full bg-white/30 cursor-pointer"><div className="h-full rounded-full bg-[#C86A51]" style={{ width: `${dur ? (cur / dur) * 100 : 0}%` }} /></div>
        <div className="mt-2 flex items-center gap-3 text-xs">
          <button type="button" data-testid="yt-guide-play-btn" onClick={toggle} className="grid place-items-center w-8 h-8 rounded-full bg-white/15 hover:bg-white/25">{playing ? <Pause size={14} /> : <Play size={14} />}</button>
          <button type="button" data-testid="yt-guide-mute-btn" onClick={toggleMute} className="grid place-items-center w-8 h-8 rounded-full bg-white/15 hover:bg-white/25">{muted ? <VolumeX size={14} /> : <Volume2 size={14} />}</button>
          <span className="tabular-nums" data-testid="yt-guide-time">{fmt(cur)} / {fmt(dur)}</span>
          <button type="button" data-testid="yt-guide-fullscreen-btn" onClick={full} className="ml-auto grid place-items-center w-8 h-8 rounded-full bg-white/15 hover:bg-white/25"><Maximize2 size={14} /></button>
        </div>
      </div>
    </div>
  );
};
